import React, { useMemo, useState } from 'react';
import { Icon } from './StockControl';
import { resolveProductIconPath, resolveProductIconTone } from './modules/stock/productVisuals';

const PRODUCT_TABS = [
  { key: 'all', label: 'Todos' },
  { key: 'stored', label: 'Guardado' },
  { key: 'pending_inbound', label: 'Ret. pend.' },
  { key: 'inbound_transit', label: 'Tránsito in' },
  { key: 'pending_outbound', label: 'Pend. sal.' },
  { key: 'outbound_transit', label: 'Tránsito out' },
  { key: 'special', label: 'Especiales' },
];

const formatMoney = (value, currency = 'ARS') => {
  const amount = Number(value || 0);
  if (!amount) return 'Manual';
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${amount}`;
  }
};

const getDateMs = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate().getTime();
    } catch {
      return 0;
    }
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const getResolvedSpecialState = (product = {}) =>
  String(product?.stockSummary?.specialState || product?._traceWarrantyState || '')
    .trim()
    .toLowerCase();

const getResolvedSpecialQty = (product = {}) =>
  Math.max(
    0,
    Number(product?.stockSummary?.specialStateQty || 0),
    Number(product?.stockBuckets?.repairQty || 0),
    Number(product?._traceWarrantyQty || 0)
  );

const getDisplayStoredQty = (product = {}) => {
  const physicalQty = Math.max(0, Number(product?.stockBuckets?.physicalQty || 0));
  const hardTrackedSpecialQty = Math.max(
    0,
    Number(product?.stockSummary?.specialStateQty || 0),
    Number(product?.stockBuckets?.repairQty || 0)
  );
  const resolvedSpecialQty = getResolvedSpecialQty(product);
  const virtualWarrantyGap = Math.max(0, resolvedSpecialQty - hardTrackedSpecialQty);
  return Math.max(0, physicalQty - virtualWarrantyGap);
};

const getAvailableCategoryControlQty = (product = {}) => {
  const physicalQty = Math.max(0, Number(product?.stockBuckets?.physicalQty || 0));
  const pendingOutboundQty = Math.max(0, Number(product?.stockBuckets?.pendingOutboundQty || 0));
  const specialQty = getResolvedSpecialQty(product);
  return Math.max(0, physicalQty - pendingOutboundQty - specialQty);
};

const getDisplayAvailableStoredQty = (product = {}) => {
  const displayStoredQty = getDisplayStoredQty(product);
  const pendingOutboundQty = Math.max(0, Number(product?.stockBuckets?.pendingOutboundQty || 0));
  return Math.max(0, displayStoredQty - pendingOutboundQty);
};

const getPendingInboundDisplayQty = (product = {}, purchaseSummary = {}) => {
  const buckets = product?.stockBuckets || {};
  const totals = purchaseSummary?.totals || {};
  const ordered = Math.max(0, Number(totals.ordered || 0));
  const pickedUp = Math.max(0, Number(totals.pickedUp || 0));
  const received = Math.max(0, Number(totals.received || 0));
  const cancelled = Math.max(0, Number(totals.cancelled || 0));
  const derivedPending = Math.max(0, ordered - pickedUp - received - cancelled);
  return Math.max(derivedPending, Math.max(0, Number(buckets.pendingInboundQty || 0)));
};

const getInboundTransitDisplayQty = (product = {}, purchaseSummary = {}) => {
  const buckets = product?.stockBuckets || {};
  const totals = purchaseSummary?.totals || {};
  const pickedUp = Math.max(0, Number(totals.pickedUp || 0));
  const received = Math.max(0, Number(totals.received || 0));
  const derivedTransit = Math.max(0, pickedUp - received);
  return Math.max(derivedTransit, Math.max(0, Number(buckets.inboundTransitQty || 0)));
};

const getPrimaryState = (product = {}) => {
  const buckets = product.stockBuckets || {};
  const specialState = getResolvedSpecialState(product);
  if (['in_warranty', 'ready_for_pickup', 'returned', 'damaged'].includes(specialState)) return 'special';
  if (
    getResolvedSpecialQty(product) > 0 &&
    /garant|repair/i.test(String(product?.stockSummary?.lastOperationReason || product?.stockSummary?.lastOperationType || ''))
  ) {
    return 'special';
  }
  if (Number(buckets.damagedQty || 0) > 0 || getResolvedSpecialQty(product) > 0 || Number(buckets.returnedQty || 0) > 0) return 'special';
  if (Number(buckets.outboundTransitQty || 0) > 0) return 'outbound_transit';
  if (Number(buckets.pendingOutboundQty || 0) > 0) return 'pending_outbound';
  if (Number(buckets.inboundTransitQty || 0) > 0) return 'inbound_transit';
  if (Number(buckets.pendingInboundQty || 0) > 0) return 'pending_inbound';
  if (getDisplayStoredQty(product) > 0) return 'stored';
  return 'catalog';
};

const isGhostProduct = (product = {}) => {
  const buckets = product.stockBuckets || {};
  return [
    Number(buckets.pendingInboundQty || 0),
    Number(buckets.inboundTransitQty || 0),
    getDisplayStoredQty(product),
    Number(buckets.pendingOutboundQty || 0),
    Number(buckets.outboundTransitQty || 0),
    getResolvedSpecialQty(product),
    Number(buckets.returnedQty || 0),
    Number(buckets.damagedQty || 0),
  ].every((value) => value <= 0);
};

const getCategoryLabel = (product = {}) => {
  const explicit = String(product.category || '').trim();
  if (explicit) return explicit;
  const firstWord = String(product.name || '').trim().split(/\s+/)[0] || '';
  return firstWord || 'General';
};

const normalizeCategory = (value = '') => String(value).trim().toLowerCase();

const isVisualCategory = (category = '') => {
  const normalized = normalizeCategory(category);
  return normalized.includes('monitor') || normalized.includes('tele') || normalized.includes('pantalla') || normalized.includes('tv');
};

const getSubcategoryLabel = (product = {}, categoryLabel = '') => {
  if (product.model) return String(product.model).trim();
  if (product.featureLabel) return String(product.featureLabel).trim();
  if (isVisualCategory(categoryLabel) && product.inches) return `${product.inches} pulgadas`;
  if (product.brand) return String(product.brand).trim();
  if (product.sku) return String(product.sku).trim();
  return 'General';
};

const STATE_META = {
  stored: {
    label: 'Guardado',
    chip: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    tile: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    activeTile: 'border-emerald-300 bg-emerald-100 text-emerald-800 shadow-[0_0_0_1px_rgba(16,185,129,0.08)]',
  },
  pending_inbound: {
    label: 'Retiro de compra pendiente',
    chip: 'bg-amber-100 text-amber-700 border border-amber-200',
    tile: 'border-amber-200 bg-amber-50 text-amber-700',
    activeTile: 'border-amber-300 bg-amber-100 text-amber-800 shadow-[0_0_0_1px_rgba(245,158,11,0.12)]',
  },
  inbound_transit: {
    label: 'Tránsito entrada',
    chip: 'bg-sky-100 text-sky-700 border border-sky-200',
    tile: 'border-sky-200 bg-sky-50 text-sky-700',
    activeTile: 'border-sky-300 bg-sky-100 text-sky-800 shadow-[0_0_0_1px_rgba(14,165,233,0.12)]',
  },
  pending_outbound: {
    label: 'Pendiente salida',
    chip: 'bg-violet-100 text-violet-700 border border-violet-200',
    tile: 'border-violet-200 bg-violet-50 text-violet-700',
    activeTile: 'border-violet-300 bg-violet-100 text-violet-800 shadow-[0_0_0_1px_rgba(139,92,246,0.12)]',
  },
  outbound_transit: {
    label: 'Tránsito salida',
    chip: 'bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200',
    tile: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700',
    activeTile: 'border-fuchsia-300 bg-fuchsia-100 text-fuchsia-800 shadow-[0_0_0_1px_rgba(217,70,239,0.12)]',
  },
  special: {
    label: 'Especial',
    chip: 'bg-rose-100 text-rose-700 border border-rose-200',
    tile: 'border-rose-200 bg-rose-50 text-rose-700',
    activeTile: 'border-rose-300 bg-rose-100 text-rose-800 shadow-[0_0_0_1px_rgba(244,63,94,0.12)]',
  },
  catalog: {
    label: 'Sin stock',
    chip: 'bg-slate-800/90 text-white border border-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.04)]',
    tile: 'border-slate-200 bg-slate-50 text-slate-600',
    activeTile: 'border-slate-300 bg-slate-100 text-slate-700 shadow-[0_0_0_1px_rgba(15,23,42,0.06)]',
  },
};

const impactCopy = (stateKey, product) => {
  const buckets = product.stockBuckets || {};
  const specialState = getResolvedSpecialState(product);
  if (stateKey === 'stored') return 'Impacta físico';
  if (stateKey === 'pending_inbound') return 'Pend. ingreso';
  if (stateKey === 'inbound_transit') return 'Entrando';
  if (stateKey === 'pending_outbound') return 'Comprometido';
  if (stateKey === 'outbound_transit') return 'Saliendo';
  if (stateKey === 'special') {
    if (specialState === 'ready_for_pickup') return 'Retiro garantía';
    if (specialState === 'in_warranty') return 'En garantía';
    if (getResolvedSpecialQty(product) > 0) return 'En garantía';
    if (Number(buckets.returnedQty || 0) > 0) return 'Vuelta garantía';
    if (Number(buckets.damagedQty || 0) > 0) return 'Rotura';
  }
  return 'Operativo';
};

const buildControlLabel = (product = {}) => {
  const status = product.controlStatus?.status;
  if (status === 'in_control') return 'En control';
  if (product.controlFrequency === 'manual') return 'Manual';
  if (product.controlFrequency === 'weekly') return 'Semanal';
  if (product.controlFrequency === 'bi-weekly') return 'Quincenal';
  if (product.controlFrequency === 'monthly') return 'Mensual';
  return 'Sin frecuencia';
};

const buildMetaLine = (product = {}) => {
  const parts = [];
  const pushUnique = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized || parts.includes(normalized)) return;
    parts.push(normalized);
  };
  pushUnique(product.model);
  pushUnique(product.brand);
  pushUnique(product.featureLabel);
  if (product.inches) pushUnique(`${product.inches} pulgadas`);
  if (!parts.length && product.sku) parts.push(product.sku);
  if (!parts.length && product.category) parts.push(product.category);
  return parts.join(' · ');
};

const formatTraceLabel = (status = '') => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'applied') return 'Aplicado';
  if (normalized === 'pending') return 'Pendiente';
  if (normalized === 'cancelled') return 'Cancelado';
  return normalized.replace(/_/g, ' ');
};

const formatOperationReason = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  const labels = {
    sale: 'Venta',
    warranty: 'Garantía',
    guarantee: 'Garantía',
    repair: 'Reparación',
    replacement: 'Reposición',
    transfer: 'Traslado',
    loan: 'Préstamo',
    return: 'Devolución',
    storage: 'Guardado',
  };
  return labels[normalized] || normalized.replace(/_/g, ' ');
};

const formatElapsedDays = (value) => {
  const dateMs = getDateMs(value);
  if (!dateMs) return '';
  const diffDays = Math.max(0, Math.floor((Date.now() - dateMs) / 86400000));
  if (diffDays === 0) return 'hoy';
  if (diffDays === 1) return '1 día';
  return `${diffDays} días`;
};

const buildStateReasonLabel = (stateKey, product = {}) => {
  const reasonLabel = formatOperationReason(product?.stockSummary?.lastOperationReason || product?.stockSummary?.lastOperationType);
  const specialState = getResolvedSpecialState(product);
  if (!reasonLabel) return '';
  if (stateKey === 'pending_outbound') return `${reasonLabel} salida`;
  if (stateKey === 'outbound_transit') return `${reasonLabel} en tránsito`;
  if (stateKey === 'special' && /garant/i.test(reasonLabel)) {
    return specialState === 'ready_for_pickup' || product?.stockSummary?.lastMovementType === 'stock_warranty_pickup_ready'
      ? 'Retiro garantía'
      : 'En garantía';
  }
  return '';
};

const getActiveStateAmount = (product = {}, purchaseSummary = {}) => {
  const buckets = product.stockBuckets || {};
  const stateKey = getPrimaryState(product);
  if (stateKey === 'pending_inbound') return getPendingInboundDisplayQty(product, purchaseSummary);
  if (stateKey === 'inbound_transit') return getInboundTransitDisplayQty(product, purchaseSummary);
  if (stateKey === 'stored') return getDisplayStoredQty(product);
  if (stateKey === 'pending_outbound') return Number(buckets.pendingOutboundQty || 0);
  if (stateKey === 'outbound_transit') return Number(buckets.outboundTransitQty || 0);
  if (stateKey === 'special') {
    if (getResolvedSpecialState(product) === 'ready_for_pickup') {
      return getResolvedSpecialQty(product);
    }
    if (getResolvedSpecialState(product) === 'in_warranty') {
      return getResolvedSpecialQty(product);
    }
    if (getResolvedSpecialQty(product) > 0) return getResolvedSpecialQty(product);
    if (Number(buckets.returnedQty || 0) > 0) return Number(buckets.returnedQty || 0);
    if (Number(buckets.damagedQty || 0) > 0) return Number(buckets.damagedQty || 0);
  }
  return 0;
};

const buildStateDetail = (stateKey, product = {}, purchaseSummary = {}) => {
  const buckets = product.stockBuckets || {};
  const totals = purchaseSummary?.totals || {};
  const reasonLabel = formatOperationReason(product?.stockSummary?.lastOperationReason || product?.stockSummary?.lastOperationType);
  const destinationLabel = String(product?.stockSummary?.lastDestinationNameSnapshot || product?._traceWarrantyDestination || '').trim();
  const warrantyDays = formatElapsedDays(product?.stockSummary?.warrantyEnteredAt || product?._traceWarrantyEnteredAt || product?.stockSummary?.lastMovementAt);
  const specialState = getResolvedSpecialState(product);
  if (stateKey === 'pending_inbound') return `${getPendingInboundDisplayQty(product, purchaseSummary)} pendientes de retiro de compra`;
  if (stateKey === 'inbound_transit') return `${getInboundTransitDisplayQty(product, purchaseSummary)} entrando a depósito`;
  if (stateKey === 'stored') return `${getDisplayStoredQty(product)} listas en depósito`;
  if (stateKey === 'pending_outbound') {
    return reasonLabel
      ? `${Number(buckets.pendingOutboundQty || 0)} listas para ${reasonLabel.toLowerCase()}`
      : `${Number(buckets.pendingOutboundQty || 0)} comprometidas para salida`;
  }
  if (stateKey === 'outbound_transit') {
    return reasonLabel
      ? `${Number(buckets.outboundTransitQty || 0)} viajando por ${reasonLabel.toLowerCase()}`
      : `${Number(buckets.outboundTransitQty || 0)} viajando a entrega`;
  }
  if (stateKey === 'special') {
    if (specialState === 'ready_for_pickup') {
      const amount = getResolvedSpecialQty(product);
      return destinationLabel
        ? `${amount} listas para retiro de garantía${warrantyDays ? ` · ${warrantyDays}` : ''} · ${destinationLabel}`
        : `${amount} listas para retiro de garantía${warrantyDays ? ` · ${warrantyDays}` : ''}`;
    }
    if (specialState === 'in_warranty') {
      const amount = getResolvedSpecialQty(product);
      return destinationLabel
        ? `${amount} en garantía / reparación${warrantyDays ? ` · ${warrantyDays}` : ''} · ${destinationLabel}`
        : `${amount} en garantía / reparación${warrantyDays ? ` · ${warrantyDays}` : ''}`;
    }
    if (getResolvedSpecialQty(product) > 0) {
      const prefix =
        product?.stockSummary?.lastMovementType === 'stock_warranty_pickup_ready'
          ? `${getResolvedSpecialQty(product)} listas para retiro de garantía`
          : `${getResolvedSpecialQty(product)} en garantía / reparación`;
      return destinationLabel
        ? `${prefix}${warrantyDays ? ` · ${warrantyDays}` : ''} · ${destinationLabel}`
        : `${prefix}${warrantyDays ? ` · ${warrantyDays}` : ''}`;
    }
    if (Number(buckets.returnedQty || 0) > 0) {
      return destinationLabel
        ? `${Number(buckets.returnedQty || 0)} en vuelta de garantía / devolución · ${destinationLabel}`
        : `${Number(buckets.returnedQty || 0)} en vuelta de garantía / devolución`;
    }
    if (Number(buckets.damagedQty || 0) > 0) return `${Number(buckets.damagedQty || 0)} con rotura`;
  }
  if (totals.ordered > 0 || totals.received > 0) {
    return 'Sin stock operativo. La historia del producto sigue disponible.';
  }
  return 'Sin movimiento todavía';
};

const DataBlock = ({ label, children, subtle, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-200 bg-slate-50/85',
    cost: 'border-rose-200 bg-rose-50/90',
    active: 'border-cyan-200 bg-cyan-50/90',
  };
  return (
    <div className={`min-w-0 rounded-2xl border px-3 py-3 ${tones[tone] || tones.slate}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <div className={`mt-1 min-w-0 ${subtle ? 'text-xs text-slate-600' : 'text-sm text-slate-800'}`}>{children}</div>
    </div>
  );
};

const QuantityTile = ({ label, value, isActive, stateKey }) => {
  const palette = STATE_META[stateKey] || STATE_META.catalog;
  return (
    <div className={`rounded-2xl border px-3 py-2 ${isActive ? palette.activeTile : palette.tile}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] opacity-75">{label}</p>
      <p className="mt-1 text-base font-bold">{Number(value || 0)}</p>
    </div>
  );
};

const ActionIconButton = ({ title, onClick, tone = 'slate', path }) => {
  const tones = {
    emerald: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-100 hover:bg-emerald-400/20',
    indigo: 'border-indigo-400/30 bg-indigo-400/10 text-indigo-100 hover:bg-indigo-400/20',
    sky: 'border-sky-400/30 bg-sky-400/10 text-sky-100 hover:bg-sky-400/20',
    rose: 'border-rose-400/30 bg-rose-400/10 text-rose-100 hover:bg-rose-400/20',
    slate: 'border-white/15 bg-white/5 text-slate-100 hover:bg-white/10',
  };
  return (
    <button
      onClick={onClick}
      title={title}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition ${tones[tone] || tones.slate}`}
      aria-label={title}
    >
      <Icon path={path} className="h-4 w-4" />
    </button>
  );
};

const ProductCard = ({ product, purchaseSummary, onStartControl, onOpenProductCycle, onEdit, onDelete, animationIndex = 0 }) => {
  const stateKey = getPrimaryState(product);
  const stateMeta = STATE_META[stateKey] || STATE_META.catalog;
  const isGhost = isGhostProduct(product);
  const totals = purchaseSummary?.totals || {};
  const traceBits = [];
  const stateReasonLabel = buildStateReasonLabel(stateKey, product);
  const shouldPulseReason =
    stateKey === 'special' &&
    getResolvedSpecialQty(product) > 0 &&
    ['stock_outbound_delivered', 'stock_warranty_pickup_ready'].includes(String(product?.stockSummary?.lastMovementType || '').trim());
  if (stateKey === 'special' && product?.stockSummary?.lastDestinationNameSnapshot) {
    traceBits.push(product.stockSummary.lastDestinationNameSnapshot);
  }
  if (product?.stockSummary?.lastRouteCodeSnapshot) traceBits.push(`Ruta ${product.stockSummary.lastRouteCodeSnapshot}`);
  else if (purchaseSummary?.latestLine?.lastRouteCodeSnapshot) traceBits.push(`Ruta ${purchaseSummary.latestLine.lastRouteCodeSnapshot}`);
  if (stateReasonLabel) traceBits.push(stateReasonLabel);
  if (product?.stockSummary?.lastMovementType) traceBits.push(`Mov. ${formatTraceLabel(product.stockSummary.lastMovementType)}`);
  else if (purchaseSummary?.latestLine?.lastMovementStatus) traceBits.push(`Mov. ${formatTraceLabel(purchaseSummary.latestLine.lastMovementStatus)}`);
  if (product.stockSummary?.lastMovementAt) {
    const movementDate = new Date(getDateMs(product.stockSummary.lastMovementAt));
    if (!Number.isNaN(movementDate.getTime())) traceBits.push(movementDate.toLocaleDateString('es-AR'));
  }
  const metaLine = buildMetaLine(product);
  const providerName = purchaseSummary?.latestLine?.supplierNameSnapshot || product.defaultSupplierName || 'Sin proveedor';
  const unitCost = Number(purchaseSummary?.latestLine?.unitCost || product.defaultPurchaseCost || 0);
  const activeAmount = getActiveStateAmount(product, purchaseSummary);
  const activeQty = Number(activeAmount || 0);
  const totalCost = Number(unitCost * activeQty || 0);
  const saleUnitPrice = Number(product.defaultSalePrice || 0);
  const saleTotal = Number(saleUnitPrice * activeQty || 0);
  const totalMargin = Number(saleTotal - totalCost || 0);
  const categoryLabel = getCategoryLabel(product);
  const subcategoryLabel = getSubcategoryLabel(product, categoryLabel);
  const productIdentityLine = [categoryLabel, metaLine].filter(Boolean).join(' · ');
  const headerDelay = `${(animationIndex % 10) * 0.35}s`;
  const stateDetail = buildStateDetail(stateKey, product, purchaseSummary);
  const quantityBreakdown = [
    { label: 'Guard.', value: getDisplayAvailableStoredQty(product), stateKey: 'stored' },
    { label: 'Ret. pend.', value: getPendingInboundDisplayQty(product, purchaseSummary), stateKey: 'pending_inbound' },
    { label: 'Tránsito in', value: getInboundTransitDisplayQty(product, purchaseSummary), stateKey: 'inbound_transit' },
    { label: 'Pend.', value: Number(product?.stockBuckets?.pendingOutboundQty || 0), stateKey: 'pending_outbound' },
    { label: 'Trans.', value: Number(product?.stockBuckets?.outboundTransitQty || 0), stateKey: 'outbound_transit' },
    {
      label: 'Garant.',
      value: getResolvedSpecialQty(product),
      stateKey: 'special',
    },
    { label: 'Vuelta', value: Number(product?.stockBuckets?.returnedQty || 0), stateKey: 'special' },
    { label: 'Rotura', value: Number(product?.stockBuckets?.damagedQty || 0), stateKey: 'special' },
  ].filter((item) => item.value > 0);
  const primaryStateLabel =
    stateKey === 'special'
      ? impactCopy(stateKey, product)
      : stateMeta.label;
  const salesHint = stateKey === 'pending_outbound' || stateKey === 'outbound_transit'
    ? stateReasonLabel || 'Salida operativa ya vinculada'
    : 'Base comercial lista para salida';

  return (
    <div className={`overflow-hidden rounded-[26px] border shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${isGhost ? 'border-slate-200/90 bg-slate-50/90 opacity-85' : 'border-slate-200 bg-white'}`}>
      <div className={`flex items-center gap-3 overflow-hidden px-4 py-3 text-white ${isGhost ? 'bg-[linear-gradient(90deg,#1f2937_0%,#0f172a_55%,#020617_100%)]' : 'bg-[linear-gradient(90deg,#475569_0%,#334155_22%,#172033_58%,#020617_100%)]'}`}>
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${resolveProductIconTone(product)} shadow-sm`}>
          <Icon path={resolveProductIconPath(product)} className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1 flex items-center gap-2 overflow-hidden">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2 overflow-hidden">
              <p
                className="shine-product-name truncate text-[15px] font-bold tracking-[0.01em]"
                style={{ animationDelay: headerDelay }}
              >
                {product.name}
              </p>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${stateMeta.chip}`}>
                {primaryStateLabel}
              </span>
              {stateReasonLabel ? (
                <span className={`shrink-0 rounded-full border border-fuchsia-300/30 bg-fuchsia-400/12 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-fuchsia-100 ${shouldPulseReason ? 'animate-pulse' : ''}`}>
                  {stateReasonLabel}
                </span>
              ) : null}
            </div>
            <p className="mt-1 truncate text-[11px] font-medium text-slate-200/90">
              {productIdentityLine || categoryLabel}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <ActionIconButton title="Iniciar control" onClick={() => onStartControl(product.id)} tone="emerald" path="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
          <ActionIconButton title="Trazabilidad del producto" onClick={() => onOpenProductCycle(product)} tone="indigo" path="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          <ActionIconButton title="Editar" onClick={() => onEdit(product)} tone="sky" path="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
          <ActionIconButton title="Eliminar" onClick={() => onDelete(product)} tone="rose" path="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397" />
        </div>
      </div>

      <div className={`px-4 py-3 ${isGhost ? 'bg-[linear-gradient(180deg,rgba(248,250,252,0.96),rgba(241,245,249,0.98))]' : ''}`}>
        <div className="grid grid-cols-1 gap-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 md:grid-cols-[minmax(0,1.25fr)_minmax(0,0.65fr)_minmax(0,0.95fr)_minmax(0,1.05fr)_minmax(0,1.15fr)]">
          <p className="truncate">Proveedor / modelo</p>
          <p className="truncate">Cant.</p>
          <p className="truncate">Compra</p>
          <p className="truncate">Salida / margen</p>
          <p className="truncate">Impacto / traza</p>
        </div>

        <div className="mt-2 grid grid-cols-1 gap-3 text-sm text-slate-800 md:grid-cols-[minmax(0,1.25fr)_minmax(0,0.65fr)_minmax(0,0.95fr)_minmax(0,1.05fr)_minmax(0,1.15fr)]">
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-900">{providerName}</p>
            <p className="truncate text-xs text-slate-500">
              {productIdentityLine || `SKU ${product.sku || '—'} · ${categoryLabel}`}
            </p>
            {product.sku ? (
              <p className="mt-1 truncate text-[11px] font-medium text-slate-400">SKU {product.sku}</p>
            ) : null}
            {isGhost ? (
              <p className="mt-1 truncate text-[11px] font-medium text-slate-400">
                Sin stock activo. Se conserva como huella histórica del producto.
              </p>
            ) : null}
          </div>

          <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/85 px-3 py-2">
            {quantityBreakdown.length > 1 ? (
              <div className="grid grid-cols-2 gap-2">
                {quantityBreakdown.map((item) => (
                  <QuantityTile
                    key={`${product.id}-${item.label}`}
                    label={item.label}
                    value={item.value}
                    isActive={item.stateKey === stateKey}
                    stateKey={item.stateKey}
                  />
                ))}
              </div>
            ) : (
              <p className="truncate text-lg font-bold text-slate-900">{activeQty}</p>
            )}
            <p className="mt-2 truncate text-[11px] text-slate-500">{stateDetail}</p>
          </div>

          <div className="min-w-0 rounded-2xl border border-rose-200 bg-rose-50/90 px-3 py-2">
            <p className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-rose-500">Costo unitario</p>
            <p className="truncate font-semibold text-rose-700">{formatMoney(unitCost)}</p>
            <p className="mt-1 truncate text-[11px] text-rose-600/80">Compra total {formatMoney(totalCost)}</p>
          </div>

          <div className="min-w-0 rounded-2xl border border-emerald-200 bg-emerald-50/90 px-3 py-2">
            <p className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-600">Salida manual</p>
            {saleUnitPrice > 0 ? (
              <>
                <p className="truncate font-semibold text-emerald-700">{formatMoney(saleUnitPrice)} c/u</p>
                <p className="mt-1 truncate text-[11px] text-emerald-700/90">Venta total {formatMoney(saleTotal)}</p>
                <p className={`mt-1 truncate text-[11px] font-semibold ${totalMargin >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                  Margen {formatMoney(totalMargin)}
                </p>
              </>
            ) : (
              <>
                <p className="truncate font-semibold text-emerald-700">Definí precio de salida</p>
                <p className="mt-1 truncate text-[11px] text-emerald-700/80">{salesHint}</p>
              </>
            )}
          </div>

          <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/90 px-3 py-2">
            <p className="truncate font-medium text-slate-800">{traceBits.length ? traceBits.join(' · ') : impactCopy(stateKey, product)}</p>
            <p className="mt-1 truncate text-[11px] text-slate-500">{stateDetail}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductsPanel = ({
  products,
  purchaseLines = [],
  stockOutboundOrders = [],
  searchTerm,
  setSearchTerm,
  onGoToPurchases,
  handleStartControl,
  handleStartCategoryControl,
  handleOpenProductCycle,
  handleEditProductClick,
  handleDeleteProductClick,
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const [categorySearch, setCategorySearch] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState('');

  const purchaseSummaryByProduct = useMemo(() => {
    return purchaseLines.reduce((acc, line) => {
      const productId = line.productId;
      if (!productId) return acc;
      if (!acc[productId]) {
        acc[productId] = {
          latestLine: null,
          totals: {
            ordered: 0,
            assigned: 0,
            pending: 0,
            pickedUp: 0,
            received: 0,
            cancelled: 0,
            totalCost: 0,
          },
        };
      }
      acc[productId].totals.ordered += Number(line.orderedQuantity || 0);
      acc[productId].totals.assigned += Number(line.assignedQuantity || 0);
      acc[productId].totals.pending += Number(line.pendingPickupQuantity || 0);
      acc[productId].totals.pickedUp += Number(line.pickedUpQuantity || 0);
      acc[productId].totals.received += Number(line.receivedQuantity || 0);
      acc[productId].totals.cancelled += Number(line.cancelledQuantity || 0);
      acc[productId].totals.totalCost += Number(line.totalCost || 0);
      const currentTime = Math.max(getDateMs(line.updatedAt), getDateMs(line.createdAt));
      const latestTime = Math.max(getDateMs(acc[productId].latestLine?.updatedAt), getDateMs(acc[productId].latestLine?.createdAt));
      if (!acc[productId].latestLine || currentTime >= latestTime) {
        acc[productId].latestLine = line;
      }
      return acc;
    }, {});
  }, [purchaseLines]);

  const categoryOptions = useMemo(() => {
    const unique = new Set(products.map((product) => getCategoryLabel(product)).filter(Boolean));
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'es'));
  }, [products]);

  const warrantyTraceByProduct = useMemo(() => {
    return stockOutboundOrders.reduce((acc, order) => {
      const normalizedOperationType = String(order?.operationType || '').trim().toLowerCase();
      const normalizedStatus = String(order?.status || '').trim().toLowerCase();
      if (!['warranty', 'guarantee', 'repair'].includes(normalizedOperationType)) return acc;
      if (!['in_warranty', 'ready_for_pickup'].includes(normalizedStatus)) return acc;

      (order.lines || []).forEach((line) => {
        const productId = String(line?.productId || '').trim();
        const quantity = Math.max(0, Number(line?.quantity || 0));
        if (!productId || !quantity) return;

        const markerTime = Math.max(
          getDateMs(line?.updatedAt),
          getDateMs(order?.warrantyPickupApprovedAt),
          getDateMs(order?.warrantyEnteredAt),
          getDateMs(order?.updatedAt)
        );

        const current = acc[productId] || {
          qty: 0,
          state: normalizedStatus,
          destination: '',
          enteredAt: order?.warrantyEnteredAt || null,
          outcome: order?.warrantyOutcome || null,
          time: 0,
        };

        current.qty += quantity;
        if (normalizedStatus === 'ready_for_pickup') current.state = 'ready_for_pickup';
        if (markerTime >= current.time) {
          current.destination =
            String(order?.contactNameSnapshot || order?.destinationNameSnapshot || '').trim() || current.destination;
          current.enteredAt = order?.warrantyEnteredAt || current.enteredAt;
          current.outcome = order?.warrantyOutcome || current.outcome;
          current.time = markerTime;
        }

        acc[productId] = current;
      });

      return acc;
    }, {});
  }, [stockOutboundOrders]);

  const contextualProducts = useMemo(
    () =>
      products.map((product) => {
        const trace = warrantyTraceByProduct[String(product.id)] || null;
        if (!trace) return product;
        return {
          ...product,
          _traceWarrantyQty: trace.qty,
          _traceWarrantyState: trace.state,
          _traceWarrantyDestination: trace.destination,
          _traceWarrantyEnteredAt: trace.enteredAt,
          _traceWarrantyOutcome: trace.outcome,
        };
      }),
    [products, warrantyTraceByProduct]
  );

  const activeCategory = useMemo(() => {
    const normalizedCategorySearch = normalizeCategory(categorySearch);
    if (normalizedCategorySearch) {
      const exactCategory = categoryOptions.find((option) => normalizeCategory(option) === normalizedCategorySearch);
      if (exactCategory) return exactCategory;
      const matchingCategories = categoryOptions.filter((option) => normalizeCategory(option).includes(normalizedCategorySearch));
      if (matchingCategories.length === 1) return matchingCategories[0];
    }
    const normalizedSearch = normalizeCategory(searchTerm);
    if (!normalizedSearch) return null;
    const searchCategory = categoryOptions.find((option) => normalizeCategory(option) === normalizedSearch);
    return searchCategory || null;
  }, [categoryOptions, categorySearch, searchTerm]);

  const subcategoryOptions = useMemo(() => {
    if (!activeCategory) return [];
    const unique = new Set(
      contextualProducts
        .filter((product) => getCategoryLabel(product) === activeCategory)
        .map((product) => getSubcategoryLabel(product, activeCategory))
        .filter(Boolean),
    );
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'es'));
  }, [contextualProducts, activeCategory]);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = String(searchTerm || '').trim().toLowerCase();
    const normalizedCategory = String(categorySearch || '').trim().toLowerCase();
    const normalizedSubcategory = String(subcategoryFilter || '').trim().toLowerCase();
    return contextualProducts.filter((product) => {
      const stateKey = getPrimaryState(product);
      const passesTab = activeTab === 'all' ? true : stateKey === activeTab;
      if (!passesTab) return false;
      const categoryLabel = getCategoryLabel(product).toLowerCase();
      if (normalizedCategory && !categoryLabel.includes(normalizedCategory)) return false;
      if (activeCategory && getCategoryLabel(product) !== activeCategory) return false;
      if (normalizedSubcategory) {
        const subcategoryLabel = getSubcategoryLabel(product, getCategoryLabel(product)).toLowerCase();
        if (!subcategoryLabel.includes(normalizedSubcategory)) return false;
      }
      if (!normalizedSearch) return true;
      const haystack = [
        product.name,
        product.sku,
        product.category,
        product.brand,
        product.featureLabel,
        product.model,
        product.notes,
        product.inches ? `${product.inches}` : '',
        purchaseSummaryByProduct[product.id]?.latestLine?.supplierNameSnapshot,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    }).sort((a, b) => {
      const aGhost = isGhostProduct(a);
      const bGhost = isGhostProduct(b);
      if (aGhost !== bGhost) return aGhost ? 1 : -1;
      const movementDiff = getDateMs(b?.stockSummary?.lastMovementAt) - getDateMs(a?.stockSummary?.lastMovementAt);
      if (movementDiff !== 0) return movementDiff;
      return String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' });
    });
  }, [contextualProducts, searchTerm, activeTab, purchaseSummaryByProduct, categorySearch, subcategoryFilter, activeCategory]);

  const categoryVisibleTotal = useMemo(() => {
    if (!activeCategory) return 0;
    return filteredProducts.reduce((acc, product) => {
      const summary = purchaseSummaryByProduct[product.id];
      return acc + Number(getActiveStateAmount(product, summary) || 0);
    }, 0);
  }, [activeCategory, filteredProducts, purchaseSummaryByProduct]);

  const categoryControlCandidates = useMemo(
    () =>
      filteredProducts.filter((product) => getAvailableCategoryControlQty(product) > 0),
    [filteredProducts]
  );

  const tabCounts = useMemo(() => PRODUCT_TABS.reduce((acc, tab) => {
    acc[tab.key] = tab.key === 'all'
      ? contextualProducts.length
      : contextualProducts.filter((product) => getPrimaryState(product) === tab.key).length;
    return acc;
  }, {}), [contextualProducts]);

  return (
    <div className="flex h-full flex-col rounded-[28px] border border-slate-200 bg-white shadow-sm">
      <style>{`
        @keyframes productNameShine {
          0% { background-position: 180% 0; }
          100% { background-position: -40% 0; }
        }
        .shine-product-name {
          background-image: linear-gradient(105deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.92) 35%, rgba(255,255,255,0.35) 46%, rgba(255,255,255,1) 53%, rgba(255,255,255,0.92) 62%, rgba(255,255,255,0.92) 100%);
          background-size: 240% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: productNameShine 3s linear infinite;
        }
      `}</style>
      <div className="border-b border-slate-200 px-5 py-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-600">Inventario operativo</p>
            <h2 className="mt-1 text-2xl font-bold text-slate-900">Productos</h2>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative min-w-[260px] flex-1">
              <input
                type="text"
                placeholder="Buscar producto, marca, característica o proveedor"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-cyan-400 focus:bg-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
                <Icon path="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z" className="h-4 w-4" />
              </div>
            </div>
            <div className="relative min-w-[220px]">
              <input
                type="text"
                placeholder="Categoría"
                list="products-category-list"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-cyan-400 focus:bg-white"
                value={categorySearch}
                onChange={(e) => {
                  setCategorySearch(e.target.value);
                  setSubcategoryFilter('');
                }}
              />
              <datalist id="products-category-list">
                {categoryOptions.map((option) => <option key={option} value={option} />)}
              </datalist>
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
                <Icon path="M3.75 4.5h16.5m-13.5 7.5h10.5m-7.5 7.5h4.5" className="h-4 w-4" />
              </div>
            </div>
            {activeCategory && subcategoryOptions.length > 0 ? (
              <div className="relative min-w-[220px]">
                <input
                  type="text"
                  list="products-subcategory-list"
                  placeholder={isVisualCategory(activeCategory) ? 'Pulgadas / modelo' : 'Modelo / subfiltro'}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-cyan-400 focus:bg-white"
                  value={subcategoryFilter}
                  onChange={(e) => setSubcategoryFilter(e.target.value)}
                />
                <datalist id="products-subcategory-list">
                  {subcategoryOptions.map((option) => <option key={option} value={option} />)}
                </datalist>
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
                  <Icon path="M4.5 6.75h15m-12 5.25h9m-6 5.25h3" className="h-4 w-4" />
                </div>
              </div>
            ) : null}
            <button
              onClick={onGoToPurchases}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800"
            >
              <Icon path="M12 4.5v15m7.5-7.5h-15" className="h-4 w-4" />
              <span>Nuevo ingreso</span>
            </button>
          </div>
        </div>

        {activeCategory ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-800">
              <span>{activeCategory}</span>
              <span className="rounded-full bg-white px-2 py-0.5 text-xs text-cyan-700">{categoryVisibleTotal}</span>
            </div>
            <button
              type="button"
              onClick={() => handleStartCategoryControl?.(activeCategory, categoryControlCandidates)}
              disabled={categoryControlCandidates.length === 0}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                categoryControlCandidates.length > 0
                  ? 'border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'
                  : 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400'
              }`}
            >
              <Icon path="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" className="h-4 w-4" />
              Controlar categoría
              <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-bold text-violet-700">
                {categoryControlCandidates.length}
              </span>
            </button>
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          {PRODUCT_TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${isActive ? 'bg-slate-900 text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                <span>{tab.label}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs ${isActive ? 'bg-white/15 text-white' : 'bg-white text-slate-500'}`}>{tabCounts[tab.key] || 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5">
        {filteredProducts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500">
            No hay productos para mostrar en este estado.
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProducts.map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                animationIndex={index}
                purchaseSummary={purchaseSummaryByProduct[product.id]}
                onStartControl={handleStartControl}
                onOpenProductCycle={handleOpenProductCycle}
                onEdit={handleEditProductClick}
                onDelete={handleDeleteProductClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPanel;
